/**
 * Common types for geo operations
 *
 * These types are canonical and vendor-agnostic. Provider implementations
 * translate to/from their specific API formats.
 */

/** WGS84 coordinates */
export interface LatLng {
  latitude: number
  longitude: number
}

/** Base error for all geo operations */
export class GeoError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
    public readonly details?: Record<string, unknown>
  ) {
    super(message)
    this.name = 'GeoError'
  }
}

/** Error thrown when geocoding fails */
export class GeocodingError extends GeoError {
  constructor(
    message: string,
    code?: string,
    public readonly address?: string,
    details?: Record<string, unknown>
  ) {
    super(message, code, details)
    this.name = 'GeocodingError'
  }
}

/** Error thrown when routing/directions fails */
export class RoutingError extends GeoError {
  constructor(
    message: string,
    code?: string,
    public readonly origin?: LatLng,
    public readonly destination?: LatLng,
    details?: Record<string, unknown>
  ) {
    super(message, code, details)
    this.name = 'RoutingError'
  }
}

/** Error thrown when address suggestion fails */
export class SuggestionError extends GeoError {
  constructor(
    message: string,
    code?: string,
    public readonly input?: string,
    details?: Record<string, unknown>
  ) {
    super(message, code, details)
    this.name = 'SuggestionError'
  }
}

/** Error thrown when API request fails at HTTP level */
export class GeoApiError extends GeoError {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly response?: unknown
  ) {
    super(message, `HTTP_${statusCode}`)
    this.name = 'GeoApiError'
  }
}

/** Error thrown when API rate limit is exceeded */
export class GeoRateLimitError extends GeoError {
  constructor(
    message: string,
    public readonly retryAfterMs?: number
  ) {
    super(message, 'RATE_LIMITED')
    this.name = 'GeoRateLimitError'
  }
}
